// Display helpers shared by every screen.

export function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { maximumFractionDigits: 3 });
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  }
  // objects read as "Key: value · Key: value" (ids left out), never as JSON
  if (Array.isArray(v)) return v.length ? v.map((x) => (x && typeof x === 'object' ? fmt(x) : String(x))).join(v.some((x) => x && typeof x === 'object') ? '; ' : ', ') : '—';
  if (typeof v === 'object') {
    const parts = Object.entries(v as Record<string, unknown>).filter(([k, x]) => !/(^|_)id$/.test(k) && x !== null && x !== undefined && x !== '' && !(Array.isArray(x) && !x.length))
      .map(([k, x]) => `${label(k)}: ${fmt(x)}`);
    return parts.length ? parts.join(' · ') : '—';
  }
  return String(v);
}

export const label = (k: string) => k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
export const usd = (v: unknown) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
export const pct = (v: unknown) => (v === null || v === undefined ? '—' : `${Math.round(Number(v) * 1000) / 10}%`);

/** Renders a policy body as plain text lines: what the approver reads (F18 approval rule 1). */
export function bodyLines(body: unknown, prefix = ''): string[] {
  if (body === null || typeof body !== 'object') return [`${prefix}${String(body)}`];
  const out: string[] = [];
  for (const [k, v] of Object.entries(body as Record<string, unknown>)) {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) out.push(`${prefix}${k}:`, ...bodyLines(v, `${prefix}  `));
    else out.push(`${prefix}${k}: ${Array.isArray(v) ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') : String(v)}`);
  }
  return out;
}

export type DiffLine = { kind: 'same' | 'added' | 'removed'; text: string };
/** A line diff (LCS) between the previous and the proposed body: the machine diff the approver sees. */
export function diffLines(before: string[], after: string[]): DiffLine[] {
  const m = before.length, n = after.length;
  const L = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i]![j] = before[i] === after[j] ? L[i + 1]![j + 1]! + 1 : Math.max(L[i + 1]![j]!, L[i]![j + 1]!);
  const out: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (before[i] === after[j]) { out.push({ kind: 'same', text: before[i]! }); i++; j++; }
    else if (L[i + 1]![j]! >= L[i]![j + 1]!) out.push({ kind: 'removed', text: before[i++]! });
    else out.push({ kind: 'added', text: after[j++]! });
  }
  while (i < m) out.push({ kind: 'removed', text: before[i++]! });
  while (j < n) out.push({ kind: 'added', text: after[j++]! });
  return out;
}

const ROLE_MAILBOX = new Set(['info', 'sales', 'hello', 'hi', 'contact', 'team', 'admin', 'support', 'office', 'mail', 'marketing', 'growth', 'founders', 'founder', 'ceo', 'ops', 'billing', 'accounts', 'finance', 'hr', 'jobs', 'careers', 'press', 'help', 'noreply', 'no-reply', 'delivered', 'test', 'me', 'user', 'enquiries', 'inquiries', 'biz', 'business', 'partners', 'dev', 'tech', 'it']);
/** A first name from a login address, or '' for a role or system mailbox (info@, sales@, delivered@…): Home said
 * "Good evening, Delivered" and the chat signed the person's messages "Delivered". */
export function firstName(email: string | null | undefined): string {
  const name = (email ?? '').split('@')[0]?.replace(/[+._-].*$/, '') ?? '';
  return name.length > 1 && !ROLE_MAILBOX.has(name.toLowerCase()) && !/\d/.test(name) ? name[0]!.toUpperCase() + name.slice(1) : '';
}
